/**
 * ContentDetailPage (retained dynamic page)
 * Firestore-backed detail page for a single content document.
 *
 * Visibility rules:
 * - published content is public
 * - draft content is visible only to `admin`/`editor`
 *
 * Status:
 * - mounted for public content detail routes
 * - also used by admin/editor preview workflows
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import LoadingState from '../components/common/LoadingState';
import { useAuth } from '../hooks/useAuth';
import { getContentById } from '../services/contentService';
import { USER_ROLES } from '../services/userService';
import { getContentConfig } from '../utils/contentConfig';
import { inferMediaKind } from '../utils/media';
import { formatDate } from '../utils/time';

function renderMedia(content) {
  const blocks = [];

  if (content.coverImageUrl) {
    blocks.push(
      <img
        key="cover-image"
        className="detail-image"
        src={content.coverImageUrl}
        alt={content.title}
      />
    );
  }

  if (content.thumbnailUrl) {
    blocks.push(
      <img
        key="thumbnail-image"
        className="detail-image"
        src={content.thumbnailUrl}
        alt={`${content.title} thumbnail`}
      />
    );
  }

  if (content.mediaUrl) {
    const mediaKind = inferMediaKind({
      mediaType: content.mediaType,
      mediaUrl: content.mediaUrl,
    });

    if (mediaKind === 'video') {
      blocks.push(
        <video key="main-video" className="detail-video" controls>
          <source src={content.mediaUrl} />
        </video>
      );
    } else {
      blocks.push(
        <img
          key="main-media-image"
          className="detail-image"
          src={content.mediaUrl}
          alt={content.title || 'Detail media'}
        />
      );
    }
  }

  return blocks;
}

export default function ContentDetailPage({ collectionKey }) {
  const { id } = useParams();
  const { profile } = useAuth();
  const config = getContentConfig(collectionKey);

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isSubscribed = true;

    async function loadDetail() {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await getContentById(collectionKey, id);

        if (!isSubscribed) {
          return;
        }

        if (!response) {
          setErrorMessage('Record not found.');
          return;
        }

        const canViewDraft =
          profile?.role === USER_ROLES.ADMIN || profile?.role === USER_ROLES.EDITOR;

        if (response.status !== 'published' && !canViewDraft) {
          setErrorMessage('This record is not published.');
          return;
        }

        setContent(response);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }
        setErrorMessage(error.message ?? 'Failed to load record.');
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }

    loadDetail();

    return () => {
      isSubscribed = false;
    };
  }, [collectionKey, id, profile?.role]);

  if (loading) {
    return <LoadingState label="Loading record..." />;
  }

  if (errorMessage || !content) {
    return (
      <div className="state-card">
        <p>{errorMessage || 'Record not found.'}</p>
        <Link to={config.route}>Back to {config.title}</Link>
      </div>
    );
  }

  return (
    <article className="detail-view">
      <header className="detail-header">
        <p className="eyebrow">{config.singular}</p>
        <h1>{content.title}</h1>
        <div className="detail-meta">
          <span>Status: {content.status || 'draft'}</span>
          <span>Updated: {formatDate(content.updatedAt || content.createdAt)}</span>
        </div>
      </header>

      {content.summary ? <p className="detail-summary">{content.summary}</p> : null}

      {renderMedia(content)}

      {collectionKey === 'articles' ? (
        <section className="detail-section">
          <p>Author: {content.author || 'No Author'}</p>
          {content.topic ? <p>Topic: {content.topic}</p> : null}
          {content.articleLanguage ? (
            <p>Language: {content.articleLanguage}</p>
          ) : null}
          {content.body ? <p className="long-text">{content.body}</p> : null}

          {Array.isArray(content.articleSections) &&
          content.articleSections.length > 0 ? (
            <>
              <h3>Article Sections</h3>
              {content.articleSections.map((section, index) => (
                <article key={`article-section-${index}`} className="detail-subsection">
                  {section.subHeadline ? <h4>{section.subHeadline}</h4> : null}
                  {section.body ? <p className="long-text">{section.body}</p> : null}
                  {section.imageUrl ? (
                    <figure>
                      <img
                        className="detail-image"
                        src={section.imageUrl}
                        alt={section.imageCaption || section.subHeadline || content.title}
                      />
                      {section.imageCaption ? (
                        <figcaption>{section.imageCaption}</figcaption>
                      ) : null}
                    </figure>
                  ) : null}
                </article>
              ))}
            </>
          ) : null}

          {Array.isArray(content.articleReferences) &&
          content.articleReferences.length > 0 ? (
            <>
              <h3>References</h3>
              <ul className="link-list">
                {content.articleReferences.map((reference, index) => (
                  <li key={`article-reference-${index}`}>
                    {reference.text ? <span>{reference.text} </span> : null}
                    {reference.link ? (
                      <a href={reference.link} target="_blank" rel="noreferrer noopener">
                        {reference.link}
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {Array.isArray(content.articleFootnotes) &&
          content.articleFootnotes.length > 0 ? (
            <>
              <h3>Footnotes</h3>
              <ol>
                {content.articleFootnotes.map((footnote, index) => (
                  <li key={`article-footnote-${index}`}>
                    {footnote.text ? <span>{footnote.text} </span> : null}
                    {footnote.link ? (
                      <a href={footnote.link} target="_blank" rel="noreferrer noopener">
                        {footnote.link}
                      </a>
                    ) : null}
                  </li>
                ))}
              </ol>
            </>
          ) : null}
        </section>
      ) : null}

      {collectionKey === 'animations' ? (
        <section className="detail-section">
          {content.topic ? <p>Topic: {content.topic}</p> : null}
          {content.creatorName ? <p>Creator: {content.creatorName}</p> : null}
          {content.creatorDiscipline ? (
            <p>Creator Discipline: {content.creatorDiscipline}</p>
          ) : null}
          {content.mediaType ? <p>Media Type: {content.mediaType}</p> : null}
          {content.description ? <p className="long-text">{content.description}</p> : null}
          {content.creatorStatement ? (
            <>
              <h3>Creator Statement</h3>
              <p className="long-text">{content.creatorStatement}</p>
            </>
          ) : null}
        </section>
      ) : null}

      {collectionKey === 'models' ? (
        <section className="detail-section">
          {content.videoUrl ? (
            <video className="detail-video" controls>
              <source src={content.videoUrl} />
            </video>
          ) : null}
          {content.type ? <p>Type: {content.type}</p> : null}
          {content.description ? <p className="long-text">{content.description}</p> : null}
          {content.metadata ? (
            <pre className="detail-code">{JSON.stringify(content.metadata, null, 2)}</pre>
          ) : null}
          {Array.isArray(content.attachments) && content.attachments.length > 0 ? (
            <>
              <h3>Attachments</h3>
              <ul className="link-list">
                {content.attachments.map((attachment) => (
                  <li key={attachment.path || attachment.url}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {attachment.name || attachment.path}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      {Array.isArray(content.relatedMedia) && content.relatedMedia.length > 0 ? (
        <section className="detail-section">
          <h3>Related Media</h3>
          <ul className="link-list">
            {content.relatedMedia.map((item) => (
              <li key={item.path || item.url}>
                <a href={item.url} target="_blank" rel="noreferrer noopener">
                  {item.name || item.path}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
